import { Button, buttonVariants } from "./button.tsx";
import { useUnsafeLayoutEffect } from "../../hooks/use-unsafe-layout-effect.ts";
import {
    EXPANDED_LINE_CLAMP,
    observeClampOverflow,
} from "../../lib/line-clamp.ts";
import { cn } from "../../lib/utils.ts";
import { type CSSProperties, type ReactNode, useRef, useState } from "react";

const READ_MORE_CLASS = "mt-6 h-11 w-fit px-6";

type ReadMoreLinkProps = { className: string; children: string };

interface ReadMoreExpandableProps {
    children: ReactNode;
    renderPhoneLink?: (props: ReadMoreLinkProps) => ReactNode;
    lines?: number;
    className?: string;
    contentClassName?: string;
    viewportFraction?: number;
}

export function ReadMoreExpandable({
    children,
    renderPhoneLink,
    lines = 4,
    className,
    contentClassName,
    viewportFraction,
}: ReadMoreExpandableProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [clampLines, setClampLines] = useState(lines);
    const contentRef = useRef<HTMLDivElement>(null);

    useUnsafeLayoutEffect(() => {
        const element = contentRef.current;
        if (!element) return;

        return observeClampOverflow(
            element,
            lines,
            viewportFraction,
            (nextLines, nextOverflowing) => {
                setClampLines(nextLines);
                setIsOverflowing(nextOverflowing);
            },
        );
    }, [lines, viewportFraction]);

    return (
        <div className={className}>
            <div
                ref={contentRef}
                className={cn(
                    "text-body text-doberman-black/80",
                    contentClassName,
                )}
                style={
                    {
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: isExpanded
                            ? EXPANDED_LINE_CLAMP
                            : clampLines,
                        overflow: isExpanded ? "visible" : "hidden",
                    } as CSSProperties
                }
            >
                {children}
            </div>
            {isOverflowing && (
                <>
                    <Button
                        type="button"
                        variant="muted"
                        onClick={() => setIsExpanded((prev) => !prev)}
                        className={cn(
                            READ_MORE_CLASS,
                            renderPhoneLink && "hidden md:inline-flex",
                        )}
                    >
                        {isExpanded ? "Read less" : "Read more"}
                    </Button>
                    {renderPhoneLink?.({
                        className: cn(
                            buttonVariants({ variant: "muted" }),
                            READ_MORE_CLASS,
                            "md:hidden",
                        ),
                        children: "Read more",
                    })}
                </>
            )}
        </div>
    );
}
